import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import gsap from 'gsap'
import { getRoomByUsername, getSkinByUsername } from './login'

const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('/draco/')
const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader)

const group = new THREE.Group()

const models = [
  {
    path: '/models/Avatar/Avatar_one.gltf',
    positionX: 0.2,
    positionZ: -2,
    rotationX: 0,
    rotationY: 0,
    rotationZ: 0,
  },
  {
    path: '/models/Avatar/Avatar_two.gltf',
    positionX: 2,
    positionZ: -1,
    rotationX: 0,
    rotationY: -1,
    rotationZ: 0,
  },
  {
    path: '/models/Avatar/Avatar_three.gltf',
    positionX: 1.6,
    positionZ: 1.1,
    rotationX: 0,
    rotationY: 4,
    rotationZ: 0,
  },
  {
    path: '/models/Avatar/Avatar_four.gltf',
    positionX: 0,
    positionZ: 2,
    rotationX: 0,
    rotationY: 3,
    rotationZ: 0,
  },
  {
    path: '/models/Avatar/Avatar_five.gltf',
    positionX: -2,
    positionZ: 0.5,
    rotationX: 0,
    rotationY: 1.8,
    rotationZ: 0,
  },
  {
    path: '/models/Avatar/Avatar_six.gltf',
    positionX: -1.5,
    positionZ: -1.3,
    rotationX: 0,
    rotationY: 1,
    rotationZ: 0,
  },
];

// Scene
const scene = new THREE.Scene();

export function init() {
  /**
   * Base
   */

  // Canvas
  const canvas = document.querySelector('canvas.webgl')

  /**
   * Group
   */
  // Group container
  scene.add(group)

  // Campfire
  const campfire = new THREE.Group()
  group.add(campfire)

  let mixer = null

  gltfLoader.load('/models/Firecamp/Firecamp.gltf', (gltf) => {
    gltf.scene.scale.set(3.5, 3.5, 3.5)
    gltf.scene.position.set(0, 0.15, 0)

    gltf.scene.traverse(function (node) {
      if (node.isMesh) {
        node.castShadow = true
      }
    })
    group.add(gltf.scene)

    mixer = new THREE.AnimationMixer(gltf.scene)
    const action = mixer.clipAction(gltf.animations[0])
    action.play()
  })

  // Logs
  const logs = new THREE.Group()
  group.add(logs)

  const logMaterial = new THREE.MeshStandardMaterial({ color: '#473924' })
  const logGeometry = new THREE.CylinderGeometry(0.25, 0.25, 0.7, 10)

  // Create logs
  for (let i = 0; i < 6; i++) {
    const angle = Math.PI * 2
    const radius = 2
    const x = Math.sin(angle + i) * radius
    const z = Math.cos(angle + i) * radius

    const log = new THREE.Mesh(logGeometry, logMaterial)
    log.position.set(x, 0.25 / 2, z)
    log.castShadow = true
    log.receiveShadow = true
    logs.add(log)
  }

  // Trees
  const trees = new THREE.Group()
  scene.add(trees)

  for (let i = 0; i < 10; i++) {
    const angle = Math.PI * 2
    const radius = 4.8
    const x = Math.sin(angle + i) * radius
    const z = Math.cos(angle + i) * radius

    gltfLoader.load('/models/Tree/Tree.gltf', (gltf) => {
      const tree = gltf.scene

      tree.position.set(x, 0, z)
      tree.scale.set(1, 1, 1)
      tree.rotation.set(0, 0, 0)

      tree.traverse(function (node) {
        if (node.isMesh) {
          node.castShadow = true
        }
      })
      trees.add(tree)
    })
  }

  /**
   * Lights
   */
  // Campfire light
  const campfireLight = new THREE.PointLight('#ff7d46', 3, 10)
  scene.add(campfireLight)
  campfireLight.position.y = 0.5

  const twinkle = gsap.timeline({
    repeat: -1,
    repeatDelay: 0.1,
    yoyo: true,
  })
  twinkle.to(campfireLight, { intensity: 3.6 })
  twinkle.to(campfireLight, { intensity: 3.3 })
  twinkle.to(campfireLight, { intensity: 3.5 })

  /**
   * Textures
   */
  // Dirt floor
  const textureLoader = new THREE.TextureLoader()

  const dirtColorTexture = textureLoader.load('/textures/ground-color.jpg')
  const dirtAOTexture = textureLoader.load('/textures/ground-ao.jpg')
  const dirtNormalTexture = textureLoader.load('/textures/ground-normal.jpg')
  const dirtHeightTexture = textureLoader.load('/textures/ground-height.png')
  const dirtRoughnessTexture = textureLoader.load(
    '/textures/ground-roughness.png'
  )

  dirtColorTexture.repeat.set(4, 4)
  dirtAOTexture.repeat.set(4, 4)
  dirtNormalTexture.repeat.set(4, 4)

  dirtColorTexture.wrapS = THREE.RepeatWrapping
  dirtAOTexture.wrapS = THREE.RepeatWrapping
  dirtNormalTexture.wrapS = THREE.RepeatWrapping

  dirtColorTexture.wrapT = THREE.RepeatWrapping
  dirtAOTexture.wrapT = THREE.RepeatWrapping
  dirtNormalTexture.wrapT = THREE.RepeatWrapping

  // Floor
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(15, 15), // 15/15 ou 10/10
    new THREE.MeshStandardMaterial({
      // color: '#3d2d1b',
      map: dirtColorTexture,
      aoMap: dirtAOTexture,
      normalMap: dirtNormalTexture,
      roughness: dirtRoughnessTexture,
    })
  )

  floor.receiveShadow = true
  floor.geometry.setAttribute(
    'uv2',
    new THREE.Float32BufferAttribute(floor.geometry.attributes.uv.array, 2)
  )
  floor.rotation.x = -Math.PI * 0.5
  floor.position.y = 0
  scene.add(floor)

  /**
   * Fog
   */
  const fog = new THREE.Fog('#000000', 1, 10)
  scene.fog = fog

  /**
   * Sizes
   */
  const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
  }

  window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  })

  /**
   * Camera
   */
  // Base camera
  const camera = new THREE.PerspectiveCamera(
    65,
    sizes.width / sizes.height,
    0.1,
    30 // 10
  )

  camera.position.x = 0
  camera.position.y = 20
  camera.position.z = 4
  camera.rotation.x = -0.5

  const sceneEntry = gsap.timeline({
    delay: 3,
  })
  sceneEntry.to(camera.position, { y: 3, duration: 2 })

  scene.add(camera)

  /**
   * Renderer
   */
  const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    powerPreference: 'high-performance',
  })
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  renderer.setSize(sizes.width, sizes.height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

  /**
   * Shadows
   */
  campfireLight.castShadow = true
  campfireLight.shadow.mapSize.set(1024, 1024)

  /**
   * Animate
   */
  let previousTime = 0

  const clock = new THREE.Clock()

  const tick = () => {
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime

    // Update mixer
    if (mixer !== null) {
      mixer.update(deltaTime)
    }

    // Rotate scene
    group.rotation.y = elapsedTime * 0.03
    floor.rotation.z = elapsedTime * 0.03
    trees.rotation.y = elapsedTime * 0.03

    // Update controls

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
  }

  tick()
}

export function loadConnectedModels(user, room) {
  if (getRoomByUsername(user) != room) {
    return
  } else {
    const modelToLoad = getSkinByUsername(user)
    gltfLoader.load(models[modelToLoad].path, (gltf) => {
      const item = gltf.scene?.children[0];

      item.position.set(
        models[modelToLoad].positionX,
        -0.5,
        models[modelToLoad].positionZ
      )
      item.rotation.set(
        models[modelToLoad].rotationX,
        models[modelToLoad].rotationY,
        models[modelToLoad].rotationZ
      )
      item.scale.set(1, 1, 1);
      item.traverse(function (node) {
        if (node.isMesh) {
          node.castShadow = true
        }
      })
      item.user_id = user;
      group.add(item)
    })
  }
}

export function unloadModel(id) {
  const toDelete = group.children.filter((child) => child.user_id == id)[0];
  group.remove(toDelete);
}
